import { Stands, type StandsKeys } from '../../Stands';
import { type AppHostNames, appHostMfs, appHosts } from '../../appHosts';
import {
	MicroFrontendsHosts,
	StaticHosts,
	appReviewHost,
	appVersionsHost,
} from '../../hosts';

import { capitalizeFirstLetter } from '@packages/utils/capitalizeFirstLetter';
import { MFFolders } from '../../MFFolders';
import { MFNames, type MFNamesKeys } from '../../MFNames';
import { devConfig } from '../../dev/dev.config';
import { isHost } from '../../utils/isHost';
import type {
	AppData,
	AppEnvs,
	DaDataKeys,
	MFLinks,
	ServiceStandHost,
	ServiceWorker,
	Services,
} from '../types';

const defaultPort = 3000;

class AppConfig<ServiceNames extends Record<string, string>> {
	private readonly defaultPort = defaultPort + Object.keys(appHostMfs).length;
	private readonly appData: AppData<ServiceNames>;
	private readonly useLocal: MFNamesKeys[];

	constructor(props: {
		servicesHosts: ServiceStandHost<ServiceNames>;
		stand: StandsKeys;
		appHostName: AppHostNames;
		serviceWorkerVersion: string;
		daDataPublicKey: string;
		captchaPublicKey: string;
		publicPath?: string;
	}) {
		const {
			stand,
			appHostName,
			publicPath,
			serviceWorkerVersion,
			daDataPublicKey,
			captchaPublicKey,
			servicesHosts,
		} = props;
		let serviceWorkerFileName = `service-worker-${stand}`;
		const taskName = process.env.taskName;
		if (process.env.NODE_ENV === 'development') {
			serviceWorkerFileName = 'service-worker';
		}
		if (taskName) {
			serviceWorkerFileName = `service-worker-${taskName}-${stand}`;
		}
		const host =
			process.env.NODE_ENV === 'development'
				? this.getLocalHost(appHostName)
				: appHosts[appHostName][stand];
		const microFrontendsHosts = MicroFrontendsHosts[stand];
		this.useLocal =
			process.env.NODE_ENV === 'development'
				? devConfig.useLocal || []
				: [];
		this.appData = {
			taskName: process.env.taskName,
			host,
			stand,
			appName: appHostName,
			captchaPublicKey,
			daDataPublicKey,
			baseUrl: taskName ? `/${taskName}/${MFFolders[appHostName]}` : '',
			rootPath: taskName ? '//' : '/',

			publicPath: this.getPublicPath(
				appHostName,
				publicPath || '/',
				stand,
				process.env.taskName,
			),
			serviceWorker: {
				version: serviceWorkerVersion,
				fileName: serviceWorkerFileName,
			},
			appVersionsUrl: appVersionsHost[stand],
			appReviewPublicPath: appReviewHost[stand],
			api: this.generateServices(stand, servicesHosts),
			static: StaticHosts[stand],
			microFrontendsHost: microFrontendsHosts,
			mfLinks: this.generateMFLinks(
				stand,
				microFrontendsHosts,
				appReviewHost[stand],
			),
		};
	}
	public get host() {
		return this.appData.host;
	}
	public get stand() {
		return this.appData.stand;
	}
	public get baseUrl() {
		return this.appData.baseUrl;
	}
	public get rootPath() {
		return this.appData.rootPath;
	}
	public get publicPath() {
		return this.appData.publicPath;
	}
	public get taskName() {
		return this.appData.taskName;
	}
	public get appReviewPublicPath() {
		return this.appData.appReviewPublicPath;
	}
	public get serviceWorker() {
		return this.appData.serviceWorker;
	}
	public get appVersionsUrl() {
		return this.appData.appVersionsUrl;
	}
	public get api() {
		return this.appData.api;
	}

	public get static() {
		return this.appData.static;
	}
	public get microFrontendsHost() {
		return this.appData.microFrontendsHost;
	}
	public get mfLinks() {
		return this.appData.mfLinks;
	}
	public get appEnvs() {
		const { api, serviceWorker, mfLinks, taskName, baseUrl, ...otherEvns } =
			this.appData;

		return otherEvns;
	}
	public getApiService(service: string) {
		return this.appData.api[service];
	}

	public getMfLink(mfLink: MFNamesKeys) {
		return this.appData.mfLinks[mfLink];
	}

	public getServiceWorkerInfo(): ServiceWorker {
		return this.appData.serviceWorker;
	}

	public getAppEnv(envName: AppEnvs): string {
		const { api, serviceWorker, mfLinks, ...otherEvns } = this.appData;

		return otherEvns[envName];
	}
	private getPublicPath(
		appHostName: AppHostNames,
		publicPath: string,
		stand: StandsKeys,
		taskName?: string,
	): string {
		if (process.env.taskName) {
			return `${appReviewHost[stand]}/${taskName}/${MFFolders[appHostName]}/`;
		}
		return publicPath;
	}
	private getLocalHost(appHostName: AppHostNames) {
		const port = defaultPort + Object.keys(appHostMfs).indexOf(appHostName);
		const host = devConfig.useExternalIP
			? devConfig.externalIp
			: 'localhost';
		return `${devConfig.useExternalIP ? 'https' : 'http'}://${host}:${port}`;
	}
	private generateMFLinks(
		stand: StandsKeys,
		microFrontendsHosts: string,
		appReviewHost: string,
	): MFLinks {
		const mfNames = Object.keys(MFNames) as MFNamesKeys[];
		let mfLinks = {} as MFLinks;
		// if (global?.location?.origin && !/\d\d\d\d$/.test(global.location.origin)) {
		// 	hostUrl =
		// 		Hosts[stand].find((hostUrl) => hostUrl.includes(global.location.origin)) ||
		// 		Hosts[stand][0];
		// }

		for (const mfName of mfNames) {
			const mfFolder = MFFolders[mfName];
			const changes = process.env.changes
				? (JSON.parse(process.env.changes) as string[])
				: [];
			if (
				process.env.taskName &&
				changes.length &&
				changes.includes(MFFolders[mfName])
			) {
				mfLinks[mfName] =
					`${appReviewHost}/${process.env.taskName}/${MFFolders[mfName]}`;
			} else {
				if (isHost(mfName)) {
					mfLinks[mfName] = appHosts[mfName as AppHostNames][stand];
				} else {
					mfLinks[mfName] = `${microFrontendsHosts}/${mfFolder}`;
				}
			}
		}
		if (process.env.NODE_ENV === 'development') {
			mfLinks = this.generateDevMFLinks(mfLinks);
		}

		return mfLinks;
	}
	private generateDevMFLinks(mfLinks: MFLinks): MFLinks {
		return this.useLocal.reduce(
			(overrideMFLinks, useLocalMfName, currentIndex) => {
				const host = devConfig.useExternalIP
					? devConfig.externalIp
					: 'localhost';
				let port = this.defaultPort + currentIndex;

				if (isHost(useLocalMfName)) {
					if (
						this.useLocal.filter((mfName) =>
							Object.keys(appHostMfs).includes(mfName),
						).length === 1
					) {
						port = defaultPort;
					} else {
						port =
							defaultPort +
							Object.keys(appHostMfs).indexOf(useLocalMfName);
					}
				}

				return Object.assign(overrideMFLinks, {
					[useLocalMfName]: `${devConfig.useExternalIP ? 'https' : 'http'}://${host}:${port}`,
				});
			},
			{ ...mfLinks },
		);
	}

	private generateServices<
		Stand extends StandsKeys,
		ServicesHosts extends ServiceStandHost<Record<string, string>>,
		ServiceNames extends Record<string, string>,
	>(stand: Stand, servicesHosts: ServicesHosts): Services<ServiceNames> {
		const services = {} as Services<ServiceNames>;
		for (const serviceName in servicesHosts) {
			const basePath = servicesHosts[serviceName][stand];
			const proxyBasePath = `/api${capitalizeFirstLetter(serviceName)}`;
			services[serviceName] = {
				basePath:
					process.env.NODE_ENV === 'development'
						? proxyBasePath
						: basePath,
				proxyBasePath:
					process.env.NODE_ENV === 'development'
						? basePath
						: proxyBasePath,
			};
		}
		return services;
	}
}

export class AppConfigs<ServiceNames extends Record<string, string>> {
	public readonly test: AppConfig<ServiceNames>;
	public readonly cert: AppConfig<ServiceNames>;
	public readonly prod: AppConfig<ServiceNames>;
	constructor(props: {
		servicesHosts: ServiceStandHost<ServiceNames>;
		appHostName: AppHostNames;
		serviceWorkerVersion: string;
		captchaPublicKey: string;
		publicPath?: string;
		daDataKeys: DaDataKeys;
	}) {
		const {
			daDataKeys,
			servicesHosts,
			appHostName,
			captchaPublicKey,

			serviceWorkerVersion,
			publicPath,
		} = props;
		this.test = new AppConfig<ServiceNames>({
			servicesHosts,
			appHostName,
			serviceWorkerVersion,
			publicPath,
			captchaPublicKey,
			daDataPublicKey: daDataKeys.test,

			stand: Stands.test,
		});
		this.cert = new AppConfig<ServiceNames>({
			servicesHosts,
			appHostName,
			serviceWorkerVersion,
			publicPath,
			captchaPublicKey,
			daDataPublicKey: daDataKeys.cert,

			stand: Stands.cert,
		});
		this.prod = new AppConfig<ServiceNames>({
			servicesHosts,
			appHostName,
			serviceWorkerVersion,
			publicPath,
			captchaPublicKey,
			daDataPublicKey: daDataKeys.prod,

			stand: Stands.prod,
		});
	}
}
